/**
 * A dónde lleva el botón "Ver" de un aviso de la campana (KAN-450). La campana no
 * confía en el enlace que armó el server: lo decide esta función y, si no lleva a
 * ningún lado, no ofrece el botón.
 *
 * - Un enlace de la app (una ruta que empieza con una sola barra, o un absoluto
 *   de este mismo origen) se abre con el router, sin recargar la página: una
 *   recarga vuelve a armar la sesión y el estado de la app desde cero.
 * - Un absoluto http(s) de otro origen se sigue con window.location.
 * - Todo lo demás (javascript:, data:, una ruta sin barra inicial, //otro-sitio,
 *   un http(s):// que no es una URL, como "https://", un texto vacío o que no es
 *   texto) no lleva a ningún lado.
 *
 * Un navegador lee la barra invertida y los caracteres de control como otra cosa
 * (/\host y /<TAB>/host son //host), y el router lo recibe como una ruta de la
 * app: por eso un enlace que los tiene no va por el router. Que hoy el router de
 * la app los deje en el inicio no alcanza: con otro router sería una redirección
 * a otro sitio.
 */
export type NotificationLinkTarget =
  | { kind: "app"; path: string }
  | { kind: "external"; href: string };

const BACKSLASH_OR_CONTROL = /[\u0000-\u001f\\]/;
const ABSOLUTE_HTTP = /^https?:\/\//i;

export function notificationLinkTarget(
  actionUrl: unknown,
  pageOrigin: string = window.location.origin
): NotificationLinkTarget | null {
  if (typeof actionUrl !== "string" || actionUrl === "") return null;
  const absolute = ABSOLUTE_HTTP.test(actionUrl);
  const maybeInApp = (actionUrl.startsWith("/") || absolute) && !actionUrl.startsWith("//") && !BACKSLASH_OR_CONTROL.test(actionUrl);
  if (maybeInApp) {
    try {
      const url = new URL(actionUrl, pageOrigin);
      const path = url.pathname + url.search + url.hash;
      // Resolver los puntos puede dejar //host (/..//host): eso no es una ruta de la app.
      if (url.origin === pageOrigin && !path.startsWith("//")) return { kind: "app", path };
    } catch {
      // No es una URL: abajo, un http(s) se vuelve a mirar solo, sin el origen.
    }
  }
  if (!absolute) return null;
  try {
    new URL(actionUrl);
  } catch {
    // Un http(s):// que no es una URL: window.location tiraría al tocar "Ver".
    return null;
  }
  return { kind: "external", href: actionUrl };
}
